import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { formatIndianDate, formatIndianDateOnly, getStatusColorClass } from '../utils/formatters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Plus, Edit, Search, Award, Check, ChevronsUpDown, AlertTriangle, Phone, ChevronLeft, ChevronRight, Wrench, ExternalLink, Download, Ban, RotateCcw, XCircle } from 'lucide-react';
import useProtectedExport from '../hooks/useProtectedExport';
import ExportPasswordDialog from '../components/ExportPasswordDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EXPORT_ALLOWED_ROLES = ["Admin"];
const CANCEL_ALLOWED_ROLES = ["Admin", "Back Office Executive"];

const LicensesManager = () => {
  const { user } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createServiceDialogOpen, setCreateServiceDialogOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [renewalFilter, setRenewalFilter] = useState('90'); // Default to show upcoming 90 days
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Check if user can export
  const canExport = EXPORT_ALLOWED_ROLES.includes(user?.role);
  const canCancel = CANCEL_ALLOWED_ROLES.includes(user?.role);
  const protectedExport = useProtectedExport();
  
  // Export CSV handler
  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/licenses/export/csv?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `licenses_stamping_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 250;
  
  const [formData, setFormData] = useState({
    customer_id: '',
    machine_device: '',
    license_type: '',
    quantity: 1,
    amount: '',
    start_date: '',
    expiry_date: '',
    renewal_due_date: '',
    description: '',
    status: 'Active'
  });

  // Service creation form
  const [serviceForm, setServiceForm] = useState({
    customer_id: '',
    service_type: 'Stamping Service',
    issue_description: '',
    priority: 'Medium',
    assigned_to: '',
    due_date: ''
  });

  // Cancel/Reactivate state
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'cancelled'
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelNotes, setCancelNotes] = useState('');
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [reactivateNotes, setReactivateNotes] = useState('');
  const [actionTargetLicense, setActionTargetLicense] = useState(null);

  // Check URL params for filters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const renewal = params.get('renewal');
    if (renewal) setRenewalFilter(renewal);
  }, []);

  useEffect(() => {
    fetchLicenses();
    fetchCustomers();
    fetchEngineers();
  }, []);

  const fetchLicenses = async () => {
    setLoading(true);
    try {
      const response = await api.get('/licenses/');
      const sortedLicenses = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setLicenses(sortedLicenses);
    } catch (error) {
      toast.error('Failed to fetch licenses');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers/');
      setCustomers(response.data.filter(c => c.status === 'Active'));
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchEngineers = async () => {
    try {
      // First try the service engineers list endpoint
      const response = await api.get('/services/engineers/list');
      setEngineers(response.data);
    } catch (error) {
      console.error('Failed to fetch engineers from services:', error);
      // Try the new assignable services endpoint
      try {
        const assignableRes = await api.get('/employees/assignable/services');
        setEngineers(assignableRes.data);
      } catch (e) {
        // Fallback to regular employees endpoint for Admin
        try {
          const fallbackResponse = await api.get('/employees/');
          const engineersList = fallbackResponse.data.filter(emp => 
            emp.is_active !== false && 
            (emp.role === 'Service Engineer' || emp.role === 'BDM & SE')
          );
          setEngineers(engineersList);
        } catch (fallbackError) {
          console.error('All fallbacks failed:', fallbackError);
        }
      }
    }
  };

  const isExpiringSoon = (renewalDate) => {
    const today = new Date();
    const renewal = new Date(renewalDate);
    const daysUntilRenewal = Math.ceil((renewal - today) / (1000 * 60 * 60 * 24));
    return daysUntilRenewal <= 30 && daysUntilRenewal >= 0;
  };

  const isExpired = (expiryDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    return expiry < today;
  };

  // Compute actual status based on renewal_due_date
  const getComputedStatus = (license) => {
    if (!license.renewal_due_date) return license.status;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const renewalDate = new Date(license.renewal_due_date);
    renewalDate.setHours(0, 0, 0, 0);
    
    // If renewal date has passed, it's expired regardless of stored status
    if (renewalDate < today) {
      return 'Expired';
    }
    
    return license.status;
  };

  // Get days until renewal (negative if overdue)
  const getDaysUntilRenewal = (renewalDate) => {
    if (!renewalDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const renewal = new Date(renewalDate);
    renewal.setHours(0, 0, 0, 0);
    return Math.ceil((renewal - today) / (1000 * 60 * 60 * 24));
  };

  // Compute renewal year label (calendar year basis)
  const getRenewalYearLabel = (renewalDate) => {
    if (!renewalDate) return null;
    const year = new Date(renewalDate).getFullYear();
    if (Number.isNaN(year)) return null;
    const currentYear = new Date().getFullYear();
    if (year === currentYear) return { label: 'This Year Renewal', className: 'bg-blue-100 text-blue-700' };
    if (year === currentYear + 1) return { label: 'Next Year Renewal', className: 'bg-purple-100 text-purple-700' };
    return { label: `${year} Renewal`, className: 'bg-gray-100 text-gray-700' };
  };

  const handleOpenDialog = (license = null) => {
    if (license) {
      setSelectedLicense(license);
      setFormData({
        customer_id: license.customer_id,
        machine_device: license.machine_device,
        license_type: license.license_type,
        quantity: license.quantity || 1,
        amount: license.amount || '',
        start_date: license.start_date,
        expiry_date: license.expiry_date,
        renewal_due_date: license.renewal_due_date,
        description: license.description || '',
        status: license.status
      });
    } else {
      setSelectedLicense(null);
      setFormData({
        customer_id: '',
        machine_device: '',
        license_type: '',
        quantity: 1,
        amount: '',
        start_date: '',
        expiry_date: '',
        renewal_due_date: '',
        description: '',
        status: 'Active'
      });
    }
    setCustomerSearchTerm('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLicense(null);
    setCustomerSearchTerm('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : null
      };
      if (selectedLicense) {
        await api.put(`/licenses/${selectedLicense.id}`, submitData);
        toast.success('Stamping/License updated successfully');
      } else {
        await api.post('/licenses/', submitData);
        toast.success('Stamping/License created successfully');
      }
      handleCloseDialog();
      fetchLicenses();
    } catch (error) {
      // Handle validation errors properly
      const errorData = error.response?.data;
      let errorMessage = 'Operation failed';
      
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Pydantic validation errors come as array
          errorMessage = errorData.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        }
      }
      toast.error(errorMessage);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Open create service dialog from stamping
  const handleOpenCreateService = (license) => {
    setSelectedLicense(license);
    
    const daysLeft = getDaysUntilRenewal(license.renewal_due_date);
    
    // Pre-fill service form with license data
    setServiceForm({
      customer_id: license.customer_id || '',
      service_type: 'Stamping/License Renewal Service',
      issue_description: `Stamping/License Renewal Service\n\nLicense No: ${license.license_no}\nMachine/Device: ${license.machine_device}\nLicense Type: ${license.license_type}\nQuantity: ${license.quantity || 1} devices\nRenewal Due: ${formatIndianDateOnly(license.renewal_due_date)}${daysLeft !== null ? ` (${daysLeft < 0 ? Math.abs(daysLeft) + ' days overdue' : daysLeft + ' days left'})` : ''}\n\nCustomer: ${license.customer?.company_name || 'N/A'}`,
      priority: daysLeft !== null && daysLeft <= 30 ? 'High' : 'Medium',
      assigned_to: '',
      due_date: license.renewal_due_date || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    });
    
    setCreateServiceDialogOpen(true);
  };

  // Create service from stamping
  const handleCreateService = async () => {
    if (!serviceForm.customer_id) {
      toast.error('Please select a customer');
      return;
    }

    setSubmitting(true);
    try {
      // Create the service with license_id for Stamping/License Renewal Service
      const response = await api.post('/services/', {
        customer_id: serviceForm.customer_id,
        service_type: serviceForm.service_type,
        description: serviceForm.issue_description || "",
        priority: serviceForm.priority,
        assigned_to: serviceForm.assigned_to || null,
        due_date: serviceForm.due_date || null,
        license_id: selectedLicense?.id || null
      });

      const createdService = response.data;
      
      // Update the license with service reference
      await api.put(`/licenses/${selectedLicense.id}`, {
        ...selectedLicense,
        service_reference: createdService.service_no,
        service_notes: `Service created: ${createdService.service_no} on ${new Date().toLocaleDateString('en-IN')}`
      });

      toast.success(`Service ${createdService.service_no} created for stamping renewal`);
      setCreateServiceDialogOpen(false);
      fetchLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create service');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to check if renewal is due within given days
  const isRenewalDueWithinDays = (renewalDate, days) => {
    if (!renewalDate) return false;
    const today = new Date();
    const renewal = new Date(renewalDate);
    const daysUntilRenewal = Math.ceil((renewal - today) / (1000 * 60 * 60 * 24));
    return daysUntilRenewal >= 0 && daysUntilRenewal <= days;
  };

  // Open cancel dialog
  const handleOpenCancelDialog = (license) => {
    setActionTargetLicense(license);
    setCancelNotes('');
    setCancelDialogOpen(true);
  };

  // Cancel license/contract
  const handleCancelLicense = async () => {
    if (!cancelNotes || cancelNotes.trim().length < 5) {
      toast.error('Cancellation notes are mandatory (minimum 5 characters)');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/licenses/${actionTargetLicense.id}/cancel`, {
        cancellation_notes: cancelNotes.trim()
      });
      toast.success(`Contract ${actionTargetLicense.license_no} cancelled successfully`);
      setCancelDialogOpen(false);
      setCancelNotes('');
      setActionTargetLicense(null);
      fetchLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel contract');
    } finally {
      setSubmitting(false);
    }
  };

  // Open reactivate dialog
  const handleOpenReactivateDialog = (license) => {
    setActionTargetLicense(license);
    setReactivateNotes('');
    setReactivateDialogOpen(true);
  };

  // Reactivate license/contract
  const handleReactivateLicense = async () => {
    setSubmitting(true);
    try {
      const resp = await api.post(`/licenses/${actionTargetLicense.id}/reactivate`, {
        reactivation_notes: (reactivateNotes || '').trim()
      });
      toast.success(resp.data?.message || `Contract ${actionTargetLicense.license_no} reactivated`);
      setReactivateDialogOpen(false);
      setReactivateNotes('');
      setActionTargetLicense(null);
      fetchLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate contract');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredLicenses = licenses
    .filter(license => {
      // Tab split: Active tab excludes Cancelled, Cancelled tab shows only Cancelled
      const isCancelled = license.status === 'Cancelled';
      if (activeTab === 'active' && isCancelled) return false;
      if (activeTab === 'cancelled' && !isCancelled) return false;

      const matchesSearch = 
        license.license_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        license.customer?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        license.customer?.mobile?.includes(searchTerm) ||
        license.machine_device?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        license.license_type?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Use computed status for filtering
      const computedStatus = getComputedStatus(license);
      const matchesStatus = activeTab === 'cancelled' || statusFilter === 'all' || computedStatus === statusFilter;
      
      // Renewal due filter (skip on Cancelled tab)
      let matchesRenewal = true;
      if (activeTab !== 'cancelled' && renewalFilter !== 'all' && license.renewal_due_date) {
        const days = parseInt(renewalFilter);
        matchesRenewal = isRenewalDueWithinDays(license.renewal_due_date, days);
      } else if (activeTab !== 'cancelled' && renewalFilter !== 'all' && !license.renewal_due_date) {
        matchesRenewal = false;
      }
      
      return matchesSearch && matchesStatus && matchesRenewal;
    })
    // Sort by renewal date (upcoming first, then overdue)
    .sort((a, b) => {
      const daysA = getDaysUntilRenewal(a.renewal_due_date);
      const daysB = getDaysUntilRenewal(b.renewal_due_date);
      
      // If both have renewal dates, sort by days until renewal
      if (daysA !== null && daysB !== null) {
        // Show urgent (0-30 days) first, then upcoming, then overdue at top
        if (daysA >= 0 && daysA <= 90 && (daysB < 0 || daysB > 90)) return -1;
        if (daysB >= 0 && daysB <= 90 && (daysA < 0 || daysA > 90)) return 1;
        return daysA - daysB;
      }
      if (daysA !== null) return -1;
      if (daysB !== null) return 1;
      return 0;
    });

  // Pagination
  const totalPages = Math.ceil(filteredLicenses.length / pageSize);
  const paginatedLicenses = filteredLicenses.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, renewalFilter, activeTab]);

  // Counts per tab
  const cancelledCount = licenses.filter(l => l.status === 'Cancelled').length;
  const activeCount = licenses.length - cancelledCount;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Stamping/License Management</h1>
          <p className="text-gray-600 mt-1">Manage stamping certificates and licenses</p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <Button variant="outline" onClick={() => protectedExport.requestExport(handleExportCSV)} data-testid="export-csv-button">
              <Download size={20} className="mr-2" />
              Export CSV
            </Button>
          )}
          <Button onClick={() => handleOpenDialog()} data-testid="add-license-button">
            <Plus size={20} className="mr-2" />
            Add Stamping/License
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Stamping/Licenses</CardTitle>
          <CardDescription>View and manage all stamping certificates and licenses</CardDescription>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="active" data-testid="tab-active-contracts">
                Active Contracts
                <Badge variant="secondary" className="ml-2">{activeCount}</Badge>
              </TabsTrigger>
              <TabsTrigger value="cancelled" data-testid="tab-cancelled-contracts">
                <Ban size={14} className="mr-1" />
                Cancelled Contracts
                <Badge variant="secondary" className="ml-2 bg-red-100 text-red-700">{cancelledCount}</Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex flex-wrap gap-4 mt-4">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search stamping/licenses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            {activeTab === 'active' && (
              <>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Expired">Expired</SelectItem>
                    <SelectItem value="Renewed">Renewed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={renewalFilter} onValueChange={setRenewalFilter}>
                  <SelectTrigger className="w-56">
                    <SelectValue placeholder="Renewal Due Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Licenses</SelectItem>
                    <SelectItem value="3">Renewal Due in 3 Days</SelectItem>
                    <SelectItem value="7">Renewal Due in 7 Days</SelectItem>
                    <SelectItem value="15">Renewal Due in 15 Days</SelectItem>
                    <SelectItem value="30">Renewal Due in 30 Days</SelectItem>
                    <SelectItem value="60">Renewal Due in 60 Days</SelectItem>
                    <SelectItem value="90">Renewal Due in 90 Days</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading && !licenses.length ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : filteredLicenses.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Award size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No licenses found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>License No.</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Machine/Device</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Renewal Due</TableHead>
                      <TableHead>Renewal Year</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedLicenses.map((license) => {
                      const daysUntilRenewal = getDaysUntilRenewal(license.renewal_due_date);
                      const computedStatus = getComputedStatus(license);
                      const isUrgent = daysUntilRenewal !== null && daysUntilRenewal >= 0 && daysUntilRenewal <= 90;
                      const isOverdue = daysUntilRenewal !== null && daysUntilRenewal < 0;
                      
                      return (
                        <TableRow 
                          key={license.id}
                          className={`
                            ${isOverdue ? 'bg-red-100' : ''}
                            ${isUrgent && !isOverdue ? 'bg-orange-50' : ''}
                          `}
                        >
                          <TableCell className="font-medium">{license.license_no}</TableCell>
                          <TableCell>{license.customer?.company_name || 'N/A'}</TableCell>
                          <TableCell>
                            {license.customer?.mobile ? (
                              <a href={`tel:${license.customer.mobile}`} className="text-blue-600 hover:underline flex items-center gap-1">
                                <Phone size={14} />
                                {license.customer.mobile}
                              </a>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{license.machine_device}</TableCell>
                          <TableCell className="text-sm max-w-[180px] truncate" title={license.description || ''}>
                            {license.description || '-'}
                          </TableCell>
                          <TableCell>{license.license_type}</TableCell>
                          <TableCell className="text-center">{license.quantity || 1}</TableCell>
                          <TableCell>{license.amount ? `₹${Number(license.amount).toLocaleString('en-IN')}` : '-'}</TableCell>
                          <TableCell className="text-sm">{formatIndianDateOnly(license.start_date)}</TableCell>
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-2">
                              {formatIndianDateOnly(license.expiry_date)}
                              {isExpired(license.expiry_date) && (
                                <AlertTriangle size={16} className="text-red-600" title="Expired" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-2">
                              <span className={isOverdue ? 'text-red-700 font-medium' : isUrgent ? 'text-orange-700 font-medium' : ''}>
                                {formatIndianDateOnly(license.renewal_due_date)}
                              </span>
                              {isOverdue && (
                                <Badge variant="destructive" className="text-xs">
                                  {Math.abs(daysUntilRenewal)}d overdue
                                </Badge>
                              )}
                              {isUrgent && !isOverdue && (
                                <Badge className="bg-orange-500 text-white text-xs">
                                  {daysUntilRenewal}d left
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {(() => {
                              const ry = getRenewalYearLabel(license.renewal_due_date);
                              return ry ? (
                                <Badge variant="outline" className={`${ry.className} border-0 text-xs whitespace-nowrap`}>
                                  {ry.label}
                                </Badge>
                              ) : '-';
                            })()}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={computedStatus === 'Active' ? 'default' : computedStatus === 'Expired' ? 'destructive' : 'secondary'}
                              className={
                                computedStatus === 'Cancelled' ? 'bg-gray-700 text-white' :
                                computedStatus === 'Expired' ? 'bg-red-600 text-white' :
                                computedStatus === 'Active' ? 'bg-green-600 text-white' :
                                'bg-blue-600 text-white'
                              }
                              title={license.cancellation_notes || ''}
                            >
                              {computedStatus}
                            </Badge>
                            {license.status === 'Cancelled' && license.cancellation_notes && (
                              <div className="text-xs text-gray-500 mt-1 max-w-xs truncate" title={license.cancellation_notes}>
                                Reason: {license.cancellation_notes}
                              </div>
                            )}
                            {license.status === 'Cancelled' && license.cancelled_by_name && (
                              <div className="text-xs text-gray-400">
                                By: {license.cancelled_by_name}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {/* Active tab actions */}
                              {activeTab === 'active' && (
                                <>
                                  {/* Show Create Service button for items <= 90 days to renewal */}
                                  {(isUrgent || isOverdue) && !license.service_reference && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleOpenCreateService(license)}
                                      className="text-blue-600 hover:bg-blue-50"
                                      title="Create Service Request"
                                    >
                                      <Wrench size={14} className="mr-1" />
                                      Service
                                    </Button>
                                  )}
                                  {/* Show linked service reference */}
                                  {license.service_reference && (
                                    <Badge className="bg-green-100 text-green-800 text-xs">
                                      <ExternalLink size={12} className="mr-1" />
                                      {license.service_reference}
                                    </Badge>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleOpenDialog(license)}
                                    title="Edit"
                                  >
                                    <Edit size={16} />
                                  </Button>
                                  {canCancel && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleOpenCancelDialog(license)}
                                      className="text-red-600 hover:bg-red-50"
                                      title="Cancel Contract"
                                      data-testid={`cancel-contract-${license.id}`}
                                    >
                                      <Ban size={16} className="mr-1" />
                                      Cancel
                                    </Button>
                                  )}
                                </>
                              )}
                              {/* Cancelled tab actions */}
                              {activeTab === 'cancelled' && canCancel && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOpenReactivateDialog(license)}
                                  className="text-green-700 border-green-300 hover:bg-green-50"
                                  data-testid={`reactivate-contract-${license.id}`}
                                >
                                  <RotateCcw size={14} className="mr-1" />
                                  Reactivate
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 px-2">
                  <div className="text-sm text-gray-500">
                    Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredLicenses.length)} of {filteredLicenses.length} records
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={16} />
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedLicense ? 'Edit Stamping/License' : 'Add New Stamping/License'}</DialogTitle>
            <DialogDescription>
              {selectedLicense ? 'Update stamping/license information' : 'Create a new stamping/license certificate'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2 col-span-2">
                <Label htmlFor="customer_id">Search Customer *</Label>
                <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={customerSearchOpen}
                      className="w-full justify-between"
                    >
                      {formData.customer_id
                        ? customers.find((customer) => customer.id === formData.customer_id)?.company_name
                        : "Search and select customer..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[600px] p-0">
                    <Command shouldFilter={false}>
                      <CommandInput 
                        placeholder="Search customer..." 
                        value={customerSearchTerm}
                        onValueChange={setCustomerSearchTerm}
                      />
                      <CommandEmpty>No customer found.</CommandEmpty>
                      <CommandGroup className="max-h-64 overflow-auto">
                        {customers
                          .filter(c => {
                            const searchLower = customerSearchTerm.toLowerCase();
                            return c.company_name.toLowerCase().includes(searchLower) ||
                                   c.contact_person.toLowerCase().includes(searchLower) ||
                                   c.mobile.includes(customerSearchTerm);
                          })
                          .map((customer) => (
                            <CommandItem
                              key={customer.id}
                              value={customer.id}
                              onSelect={() => {
                                setFormData({ ...formData, customer_id: customer.id });
                                setCustomerSearchOpen(false);
                                setCustomerSearchTerm('');
                              }}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${
                                  formData.customer_id === customer.id ? "opacity-100" : "opacity-0"
                                }`}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{customer.company_name}</span>
                                <span className="text-sm text-gray-500">
                                  {customer.contact_person} • {customer.mobile}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2 col-span-2">
                <Label htmlFor="machine_device">Machine / Device Name *</Label>
                <Input
                  id="machine_device"
                  value={formData.machine_device}
                  onChange={(e) => setFormData({ ...formData, machine_device: e.target.value })}
                  required
                  placeholder="Enter machine or software name"
                />
              </div>

              <div className="space-y-2 col-span-2">
                <Label htmlFor="license_type">License Type *</Label>
                <Input
                  id="license_type"
                  value={formData.license_type}
                  onChange={(e) => setFormData({ ...formData, license_type: e.target.value })}
                  required
                  placeholder="e.g., Annual Subscription, Perpetual License"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity of Devices *</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                  required
                  placeholder="Number of devices"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Amount (Optional)</Label>
                <Input
                  id="amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="Enter amount"
                  data-testid="license-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiry_date">Expiry Date *</Label>
                <Input
                  id="expiry_date"
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="renewal_due_date">Renewal Due Date *</Label>
                <Input
                  id="renewal_due_date"
                  type="date"
                  value={formData.renewal_due_date}
                  onChange={(e) => setFormData({ ...formData, renewal_due_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Expired">Expired</SelectItem>
                    <SelectItem value="Renewed">Renewed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 col-span-2">
                <Label htmlFor="description">Description <span className="text-xs text-gray-400">(Optional)</span></Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Additional notes or description for this stamping/license..."
                  rows={3}
                  data-testid="license-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : selectedLicense ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Service Dialog */}
      <Dialog open={createServiceDialogOpen} onOpenChange={setCreateServiceDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <Wrench className="w-5 h-5 inline mr-2" />
              Create Service for Stamping Renewal
            </DialogTitle>
          </DialogHeader>
          
          {selectedLicense && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <p className="text-sm font-medium text-amber-800">
                License: {selectedLicense.license_no}
              </p>
              <p className="text-xs text-amber-700">
                {selectedLicense.machine_device} | Qty: {selectedLicense.quantity || 1} devices | 
                Due: {formatIndianDateOnly(selectedLicense.renewal_due_date)}
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <Label>Customer *</Label>
              <Select
                value={serviceForm.customer_id}
                onValueChange={(v) => setServiceForm(prev => ({ ...prev, customer_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.company_name} - {c.mobile}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Service Type *</Label>
                <Select
                  value={serviceForm.service_type}
                  onValueChange={(v) => setServiceForm(prev => ({ ...prev, service_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Stamping/License Renewal Service">Stamping/License Renewal Service</SelectItem>
                    <SelectItem value="Onsite Field Service">Onsite Field Service</SelectItem>
                    <SelectItem value="In-Office Service">In-Office Service</SelectItem>
                    <SelectItem value="AMC/MMC Service">AMC/MMC Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select
                  value={serviceForm.priority}
                  onValueChange={(v) => setServiceForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assign To Engineer</Label>
                <Select
                  value={serviceForm.assigned_to || 'none'}
                  onValueChange={(v) => setServiceForm(prev => ({ ...prev, assigned_to: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select engineer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {engineers.map(e => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={serviceForm.due_date}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, due_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            
            <div>
              <Label>Issue Description <span className="text-xs text-gray-400">(Optional)</span></Label>
              <Textarea
                value={serviceForm.issue_description}
                onChange={(e) => setServiceForm(prev => ({ ...prev, issue_description: e.target.value }))}
                rows={5}
                placeholder="Service details (optional)..."
                data-testid="license-issue-description"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateServiceDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateService} disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ExportPasswordDialog {...protectedExport} />

      {/* Cancel Contract Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent data-testid="cancel-contract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={20} />
              Cancel Contract {actionTargetLicense?.license_no}
            </DialogTitle>
            <DialogDescription className="text-red-600">
              <strong>Warning:</strong> This contract will be cancelled and removed from the active
              contracts list. It will be moved to the "Cancelled Contracts" tab. You can reactivate it
              later if needed. Please provide a reason below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm space-y-1">
              <div><span className="text-gray-600">Customer:</span> <span className="font-medium ml-2">{actionTargetLicense?.customer?.company_name || 'N/A'}</span></div>
              <div><span className="text-gray-600">Machine/Device:</span> <span className="font-medium ml-2">{actionTargetLicense?.machine_device || '-'}</span></div>
              <div><span className="text-gray-600">License Type:</span> <span className="font-medium ml-2">{actionTargetLicense?.license_type || '-'}</span></div>
            </div>
            <div className="space-y-2">
              <Label>Cancellation Reason * <span className="text-xs text-gray-500">(minimum 5 characters)</span></Label>
              <Textarea
                value={cancelNotes}
                onChange={(e) => setCancelNotes(e.target.value)}
                placeholder="Reason for cancelling this contract..."
                rows={4}
                data-testid="cancel-contract-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              Keep Contract
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelLicense}
              disabled={submitting || !cancelNotes || cancelNotes.trim().length < 5}
              data-testid="confirm-cancel-contract"
            >
              <XCircle size={16} className="mr-2" />
              {submitting ? 'Cancelling...' : 'Confirm Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivate Contract Dialog */}
      <Dialog open={reactivateDialogOpen} onOpenChange={setReactivateDialogOpen}>
        <DialogContent data-testid="reactivate-contract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <RotateCcw size={20} />
              Reactivate Contract {actionTargetLicense?.license_no}
            </DialogTitle>
            <DialogDescription>
              This will move the contract back to Active. You can optionally add a note explaining why.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm space-y-1">
              <div><span className="text-gray-600">Customer:</span> <span className="font-medium ml-2">{actionTargetLicense?.customer?.company_name || 'N/A'}</span></div>
              <div><span className="text-gray-600">Originally cancelled because:</span> <span className="italic ml-2">{actionTargetLicense?.cancellation_notes || 'No reason recorded'}</span></div>
            </div>
            <div className="space-y-2">
              <Label>Reactivation Note <span className="text-xs text-gray-400">(optional)</span></Label>
              <Textarea
                value={reactivateNotes}
                onChange={(e) => setReactivateNotes(e.target.value)}
                placeholder="Why is this contract being reactivated?"
                rows={3}
                data-testid="reactivate-contract-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReactivateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReactivateLicense}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-reactivate-contract"
            >
              <RotateCcw size={16} className="mr-2" />
              {submitting ? 'Reactivating...' : 'Confirm Reactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LicensesManager;
